import { useState, useEffect } from 'react';
import { AdminLayout } from '../AdminLayout';
import VERSION_INFO from '../../config/version';
import { supabase } from '../supabase';
import rawSettings from '../../config/settings.json';
import BHCS_MCP_SKILL from '../../../skills/bhcs-mcp-ops/SKILL.md?raw';
import { useUrlTabState } from '../../hooks/useUrlTabState';
import { ChangelogEntries } from '../components/changelog/ChangelogEntries';
import { FlagCohortsTab } from '../components/FlagCohortsTab';

type Tab = 'changelog' | 'app-settings' | 'flag-cohorts' | 'ai-cli';
const SYSTEM_TABS: readonly Tab[] = ['changelog', 'app-settings', 'flag-cohorts', 'ai-cli'];
const TAB_LABELS: Record<Tab, string> = {
  changelog: 'Changelog',
  'app-settings': 'App Settings',
  'flag-cohorts': 'Flag Cohorts',
  'ai-cli': 'AI Connectors',
};

// Strip edgeSettings from the UI view — it has its own panel
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const { edgeSettings: _edgeSettings, ...uiSettings } = rawSettings as Record<string, unknown>;

function SettingValue({ value, depth = 0 }: { value: unknown; depth?: number }) {
  if (value === null || value === undefined) {
    return <span className="text-gray-400 italic">null</span>;
  }
  if (typeof value === 'boolean') {
    return (
      <span className={value ? 'text-green-600 font-medium' : 'text-red-500 font-medium'}>
        {value ? 'true' : 'false'}
      </span>
    );
  }
  if (typeof value === 'number') {
    return <span className="text-blue-600">{String(value)}</span>;
  }
  if (typeof value === 'string') {
    return <span className="text-gray-800">{value}</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-gray-400 italic">[]</span>;
    return (
      <ul className="mt-1 space-y-1 border-l-2 border-gray-200 pl-3">
        {value.map((item, i) => (
          <li key={i} className="text-sm">
            <SettingValue value={item} depth={depth + 1} />
          </li>
        ))}
      </ul>
    );
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return <span className="text-gray-400 italic">{'{}'}</span>;
    return (
      <div className={depth > 0 ? 'mt-1 border-l-2 border-gray-200 pl-3 space-y-2' : 'space-y-2'}>
        {entries.map(([k, v]) => (
          <div key={k} className="flex flex-col sm:flex-row sm:items-start gap-x-3">
            <span className="text-xs font-mono font-semibold text-indigo-700 min-w-[12rem] shrink-0">{k}</span>
            <div className="text-sm">
              <SettingValue value={v} depth={depth + 1} />
            </div>
          </div>
        ))}
      </div>
    );
  }
  return <span className="text-gray-800">{String(value)}</span>;
}

function SettingsPanel({ title, subtitle, value, loading, error }: {
  title: string;
  subtitle: string;
  value?: unknown;
  loading?: boolean;
  error?: string | null;
}) {
  return (
    <div>
      <div className="mb-2">
        <p className="text-sm font-medium text-gray-800">{title}</p>
        <p className="text-xs text-gray-500">{subtitle}</p>
      </div>
      <div className="bg-gray-50 border rounded-lg p-4 min-h-[4rem]">
        {loading && <span className="text-sm text-gray-400">Loading…</span>}
        {error && <span className="text-sm text-red-600">{error}</span>}
        {!loading && !error && <SettingValue value={value} />}
      </div>
    </div>
  );
}

function AppSettingsTab() {
  const [liveEdgeSettings, setLiveEdgeSettings] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const { data, error: fnErr } = await supabase.functions.invoke('get-app-settings', {
          method: 'GET',
        });
        if (fnErr) throw fnErr;
        setLiveEdgeSettings((data as { settings: unknown }).settings);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Failed to load edge settings');
      } finally {
        setLoading(false);
      }
    }
    void load();
  }, []);

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <SettingsPanel
        title="UI Settings"
        subtitle="Bundled with the frontend build — controls feature flags and UI behavior. Changes require a redeployment."
        value={uiSettings}
      />
      <SettingsPanel
        title="Edge Settings"
        subtitle="Live APP_SETTINGS secret on the edge — controls purchase limits, bonus %, and balance gate. Synced from settings.json → edgeSettings on each deploy."
        value={liveEdgeSettings}
        loading={loading}
        error={error}
      />
    </div>
  );
}

interface ApiKeyRow {
  id: string;
  name: string;
  prefix: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

async function sha256hex(raw: string): Promise<string> {
  const buf = new TextEncoder().encode(raw);
  const hashBuf = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hashBuf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

const API_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cli-api`;
const MCP_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/mcp`;


function CopyButton({ text, label = 'Copy', size = 'sm' }: { text: string; label?: string; size?: 'sm' | 'lg' }) {
  const [copied, setCopied] = useState(false);
  function handleCopy() {
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  }
  if (size === 'lg') {
    return (
      <button
        onClick={handleCopy}
        className={`w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium rounded-lg border-2 transition-all ${
          copied
            ? 'bg-green-50 border-green-400 text-green-700'
            : 'bg-white border-blue-400 text-blue-700 hover:bg-blue-50'
        }`}
      >
        <span className="text-base">{copied ? '✓' : '⎘'}</span>
        {copied ? 'Copied to clipboard!' : label}
      </button>
    );
  }
  return (
    <button
      onClick={handleCopy}
      className="shrink-0 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded border border-gray-300 transition-colors"
    >
      {copied ? '✓ Copied' : label}
    </button>
  );
}

function CodeBlock({ code, label }: { code: string; label?: string }) {
  return (
    <div className="relative mt-1 bg-gray-900 rounded-lg overflow-hidden">
      {label && <p className="text-xs text-gray-400 px-3 pt-2">{label}</p>}
      <div className="flex items-start gap-2 p-3">
        <pre className="flex-1 text-xs font-mono text-green-300 whitespace-pre-wrap break-all">{code}</pre>
        <CopyButton text={code} />
      </div>
    </div>
  );
}

function StepNum({ n, active = true }: { n: number; active?: boolean }) {
  return (
    <span className={`flex items-center justify-center w-6 h-6 rounded-full text-white text-xs font-bold shrink-0 ${active ? 'bg-blue-600' : 'bg-gray-300'}`}>
      {n}
    </span>
  );
}

function TrackStep({ n, title, active = true, children }: { n: number; title: string; active?: boolean; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <StepNum n={n} active={active} />
        <h4 className={`text-sm font-semibold ${active ? 'text-gray-800' : 'text-gray-400'}`}>{title}</h4>
      </div>
      <div className="ml-8">{children}</div>
    </div>
  );
}

function AiCliTab() {
  const [userId, setUserId] = useState<string | null>(null);
  const [keys, setKeys] = useState<ApiKeyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [newKeyName, setNewKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newRawKey, setNewRawKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [os, setOs] = useState<'mac' | 'windows'>('mac');

  useEffect(() => {
    void supabase.auth.getUser().then(({ data }) => setUserId(data.user?.id ?? null));
  }, []);

  async function loadKeys() {
    setLoading(true);
    const { data, error: err } = await supabase
      .from('api_keys')
      .select('id, name, prefix, created_at, last_used_at, revoked_at')
      .order('created_at', { ascending: false });
    setKeys((data as ApiKeyRow[]) ?? []);
    if (err) setError(err.message);
    setLoading(false);
  }

  useEffect(() => { void loadKeys(); }, []);

  async function createKey() {
    if (!newKeyName.trim() || !userId) return;
    setCreating(true);
    setError(null);
    setNewRawKey(null);
    try {
      const bytes = new Uint8Array(32);
      crypto.getRandomValues(bytes);
      const hex = Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
      const rawKey = `bhcs_sk_${hex}`;
      const keyHash = await sha256hex(rawKey);
      const prefix = rawKey.slice(0, 16);
      const { error: insertErr } = await supabase.from('api_keys').insert({
        admin_user_id: userId,
        name: newKeyName.trim(),
        key_hash: keyHash,
        prefix,
      });
      if (insertErr) throw new Error(insertErr.message);
      setNewRawKey(rawKey);
      setNewKeyName('');
      await loadKeys();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create key');
    } finally {
      setCreating(false);
    }
  }

  async function revokeKey(id: string) {
    const { error: err } = await supabase
      .from('api_keys')
      .update({ revoked_at: new Date().toISOString() })
      .eq('id', id);
    if (err) { setError(err.message); return; }
    await loadKeys();
  }

  const macConfigCmd = newRawKey
    ? `mkdir -p ~/.bhcs && cat > ~/.bhcs/config.json << 'EOF'\n{\n  "api_url": "${API_URL}",\n  "api_key": "${newRawKey}"\n}\nEOF`
    : null;

  const winConfigCmd = newRawKey
    ? `New-Item -ItemType Directory -Force "$HOME\\.bhcs" | Out-Null\n@'\n{\n  "api_url": "${API_URL}",\n  "api_key": "${newRawKey}"\n}\n'@ | Set-Content "$HOME\\.bhcs\\config.json"`
    : null;

  const hasKey = !!newRawKey;

  return (
    <div className="p-4 sm:p-6 space-y-8">

      {/* ── Common: Create API key ── */}
      <section>
        <div className="flex items-center gap-2 mb-1">
          <StepNum n={1} />
          <h3 className="text-sm font-semibold text-gray-800">Create an API key</h3>
        </div>
        <p className="text-xs text-gray-500 mb-3 ml-8">
          Name it for the machine it's for (e.g. "laptop", "work-mac"). One key works for both the CLI and Claude.ai connector.
        </p>
        <div className="ml-8 flex gap-2">
          <input
            type="text"
            placeholder='Key name, e.g. "laptop"'
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void createKey(); }}
            className="flex-1 border rounded px-3 py-2 text-sm max-w-xs"
          />
          <button
            onClick={() => void createKey()}
            disabled={creating || !newKeyName.trim()}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {creating ? 'Creating…' : 'Create key'}
          </button>
        </div>
        {error && <p className="mt-2 text-sm text-red-600 ml-8">{error}</p>}
        {newRawKey && (
          <div className="mt-4 ml-8 bg-yellow-50 border border-yellow-300 rounded-lg p-4">
            <p className="text-sm font-semibold text-yellow-800">Key created — shown once only</p>
            <p className="text-xs text-yellow-700 mt-1 mb-2">Copy it now, or follow the setup steps below — the commands will include it automatically.</p>
            <div className="flex items-center gap-2 bg-yellow-100 rounded p-2 border border-yellow-200">
              <code className="flex-1 text-xs font-mono break-all text-yellow-900">{newRawKey}</code>
              <CopyButton text={newRawKey} label="Copy key" />
            </div>
          </div>
        )}
      </section>

      {/* ── Two-track split ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* ── LEFT: Claude.ai connector track ── */}
        <div className="border-2 border-blue-200 rounded-xl p-5 space-y-6 bg-blue-50">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-semibold text-gray-900">Claude.ai Connector</h3>
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-600 text-white tracking-wide">No terminal needed</span>
            </div>
            <p className="text-xs text-gray-500">Works on any device — phone, tablet, or desktop. Requires Claude Pro or Team.</p>
          </div>

          <TrackStep n={2} title="Open Claude.ai Connectors">
            <p className="text-xs text-gray-500 mb-2">
              In Claude.ai, go to <strong>Settings → Customize → Add custom connector</strong>.
            </p>
            <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-2 text-xs text-gray-600">
              <div className="flex items-center gap-2">
                <span className="w-4 h-4 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[10px] font-bold shrink-0">1</span>
                <span>Claude.ai → <strong>Settings</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-4 h-4 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[10px] font-bold shrink-0">2</span>
                <span>Click <strong>Customize</strong> in the sidebar</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-4 h-4 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[10px] font-bold shrink-0">3</span>
                <span>Scroll to <strong>Connectors</strong> → <strong>Add custom connector</strong></span>
              </div>
            </div>
          </TrackStep>

          <TrackStep n={3} title="Add the BHCS connector" active={hasKey}>
            <p className="text-xs text-gray-500 mb-2">
              Set <strong>Name</strong> to <code className="bg-gray-100 px-1 rounded">BHCS Portal</code>, then paste the URL below into <strong>Remote MCP server URL</strong>.
            </p>
            {newRawKey ? (
              <CodeBlock label="Remote MCP server URL — paste into Claude.ai" code={`${MCP_URL}?key=${newRawKey}`} />
            ) : (
              <div className="bg-white border border-dashed border-gray-300 rounded-lg p-3 text-xs text-gray-400 text-center">
                Create a key in Step 1 — the connector URL will appear here.
              </div>
            )}
          </TrackStep>

          <TrackStep n={4} title="Install the BHCS operator skill">
            <p className="text-xs text-gray-500 mb-2">
              Teaches Claude the full BHCS playbook — every tool, workflow, business rule, and gotcha.
              Download the skill file, then upload it to Claude.ai.
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const blob = new Blob([BHCS_MCP_SKILL], { type: 'text/markdown' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `bhcs-mcp-ops-${VERSION_INFO.full}.md`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                className="shrink-0 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded border border-gray-300 transition-colors"
              >
                Download skill (.md)
              </button>
              <a
                href="https://claude.ai/customize/skills"
                target="_blank"
                rel="noreferrer"
                className="shrink-0 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded border border-gray-300 transition-colors"
              >
                Open Claude.ai Skills ↗
              </a>
            </div>
          </TrackStep>

          <TrackStep n={5} title="Start chatting">
            <p className="text-xs text-gray-500 mb-2">Open a chat in your BHCS project and try:</p>
            <div className="space-y-2">
              {[
                'Show me all outstanding invoices',
                'Run autopay dry run',
                'Who are the students enrolled in Math Olympiad?',
                'Record a $150 Zelle payment for invoice abc-123',
              ].map((prompt) => (
                <div key={prompt} className="flex items-start gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
                  <span className="text-gray-400 text-xs mt-0.5 shrink-0">💬</span>
                  <span className="text-xs text-gray-700 italic flex-1">"{prompt}"</span>
                  <CopyButton text={prompt} />
                </div>
              ))}
            </div>
          </TrackStep>
        </div>

        {/* ── RIGHT: CLI track ── */}
        <div className="border rounded-xl p-5 space-y-6 bg-gray-50">
          {/* Track header + OS toggle */}
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">CLI + Claude Code</h3>
              <p className="text-xs text-gray-500 mt-0.5">Terminal-based. Enables <code className="bg-gray-200 px-1 rounded">bhcs</code> commands and the bhcs-ops agent skill.</p>
            </div>
            <div className="flex items-center shrink-0 border border-gray-300 rounded-lg overflow-hidden text-xs font-medium">
              <button
                onClick={() => setOs('mac')}
                className={`px-3 py-1.5 transition-colors ${os === 'mac' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
              >
                macOS
              </button>
              <button
                onClick={() => setOs('windows')}
                className={`px-3 py-1.5 transition-colors ${os === 'windows' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
              >
                Windows
              </button>
            </div>
          </div>

          {os === 'mac' ? (
            <>
              <TrackStep n={2} title="Install the BHCS CLI">
                <p className="text-xs text-gray-500 mb-2">
                  Run once. Requires <a href="https://nodejs.org" target="_blank" rel="noreferrer" className="underline hover:text-blue-600">Node.js 20+</a>.
                </p>
                <CodeBlock label="Terminal" code="cd ~/src/bhcs/cli && npm install && npm run build && npm link" />
                <p className="text-xs text-gray-400 mt-1">After this, <code className="bg-gray-100 px-1 rounded">bhcs</code> is available everywhere in your shell.</p>
              </TrackStep>

              <TrackStep n={3} title="Configure the CLI" active={hasKey}>
                {macConfigCmd ? (
                  <>
                    <p className="text-xs text-gray-500 mb-2">Paste into Terminal — writes <code className="bg-gray-100 px-1 rounded">~/.bhcs/config.json</code> with your key.</p>
                    <CodeBlock label="Terminal — paste and run" code={macConfigCmd} />
                  </>
                ) : (
                  <p className="text-xs text-gray-400">Create a key in Step 1 first.</p>
                )}
              </TrackStep>

              <TrackStep n={4} title="Install bhcs-ops agent skill">
                <p className="text-xs text-gray-500 mb-2">Lets Claude Code run BHCS CLI commands on your behalf inside Claude Code sessions.</p>
                <CodeBlock label="Claude Code — type in chat" code="/install bhcs-ops" />
              </TrackStep>

              <TrackStep n={5} title="Verify">
                <CodeBlock label="Terminal" code="bhcs auth whoami" />
                <p className="text-xs text-gray-400 mt-1">Should print your email and key prefix.</p>
                <div className="mt-3 bg-blue-50 border border-blue-200 rounded p-3 space-y-1">
                  <p className="text-xs font-medium text-blue-800 mb-1">Handy first commands</p>
                  {[
                    ['List students', 'bhcs students list --active'],
                    ['Outstanding invoices', 'bhcs invoices outstanding --json'],
                    ['Autopay preview', 'bhcs batch run-autopay --dry-run'],
                  ].map(([label, cmd]) => (
                    <div key={cmd} className="flex items-center gap-2">
                      <span className="text-xs text-blue-600 w-32 shrink-0">{label}</span>
                      <code className="flex-1 text-xs font-mono text-blue-900 bg-blue-100 px-2 py-0.5 rounded">{cmd}</code>
                      <CopyButton text={cmd} />
                    </div>
                  ))}
                </div>
              </TrackStep>
            </>
          ) : (
            <>
              <TrackStep n={2} title="Install the BHCS CLI">
                <p className="text-xs text-gray-500 mb-2">
                  Run once in PowerShell. Requires <a href="https://nodejs.org" target="_blank" rel="noreferrer" className="underline hover:text-blue-600">Node.js 20+</a>.
                </p>
                <CodeBlock label="PowerShell" code={`cd $HOME\\src\\bhcs\\cli\nnpm install\nnpm run build\nnpm link`} />
              </TrackStep>

              <TrackStep n={3} title="Configure the CLI" active={hasKey}>
                {winConfigCmd ? (
                  <>
                    <p className="text-xs text-gray-500 mb-2">Run in PowerShell — writes <code className="bg-gray-100 px-1 rounded">%USERPROFILE%\.bhcs\config.json</code> with your key.</p>
                    <CodeBlock label="PowerShell — paste and run" code={winConfigCmd} />
                  </>
                ) : (
                  <p className="text-xs text-gray-400">Create a key in Step 1 first.</p>
                )}
              </TrackStep>

              <TrackStep n={4} title="Install bhcs-ops agent skill">
                <p className="text-xs text-gray-500 mb-2">Same as macOS — type this in a Claude Code chat session.</p>
                <CodeBlock label="Claude Code — type in chat" code="/install bhcs-ops" />
              </TrackStep>

              <TrackStep n={5} title="Verify">
                <CodeBlock label="PowerShell" code="bhcs auth whoami" />
                <p className="text-xs text-gray-400 mt-1">Should print your email and key prefix.</p>
                <div className="mt-3 bg-amber-50 border border-amber-200 rounded p-3">
                  <p className="text-xs text-amber-800">Windows support is functional but not fully tested. If you hit issues, reach out to the portal admin.</p>
                </div>
              </TrackStep>
            </>
          )}
        </div>

      </div>

      {/* ── Key list ── */}
      <section>
        <h3 className="text-sm font-semibold text-gray-800 mb-3">Your API keys</h3>
        {loading ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : keys.length === 0 ? (
          <p className="text-sm text-gray-400">No keys yet — create one above.</p>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Name</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Prefix</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Created</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Last used</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Status</th>
                  <th />
                </tr>
              </thead>
              <tbody className="divide-y">
                {keys.map((k) => (
                  <tr key={k.id} className={k.revoked_at ? 'opacity-40' : ''}>
                    <td className="px-4 py-2 font-medium">{k.name}</td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-500">{k.prefix}…</td>
                    <td className="px-4 py-2 text-gray-500">{new Date(k.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-2 text-gray-500">{k.last_used_at ? new Date(k.last_used_at).toLocaleDateString() : '—'}</td>
                    <td className="px-4 py-2">
                      {k.revoked_at
                        ? <span className="text-red-500 text-xs">Revoked</span>
                        : <span className="text-green-600 text-xs">Active</span>}
                    </td>
                    <td className="px-4 py-2">
                      {!k.revoked_at && (
                        <button
                          onClick={() => void revokeKey(k.id)}
                          className="text-xs text-red-500 hover:text-red-700"
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

export default function AdminSystem() {
  const [activeTab, setActiveTab] = useUrlTabState<Tab>('tab', SYSTEM_TABS, 'changelog');

  return (
    <AdminLayout>
      <div className="bg-white shadow overflow-hidden sm:rounded-lg flex flex-col min-h-0 flex-1">
        <div className="px-4 py-5 sm:px-6 border-b flex-shrink-0">
          <h1 className="text-lg font-semibold text-gray-900">System</h1>
          <p className="text-sm text-gray-600">Version {VERSION_INFO.display}</p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 flex-shrink-0">
          <nav className="flex -mb-px px-4 sm:px-6 overflow-x-auto" aria-label="Tabs">
            {SYSTEM_TABS.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`mr-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {TAB_LABELS[tab]}
              </button>
            ))}
          </nav>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
        {activeTab === 'app-settings' && <AppSettingsTab />}
        {activeTab === 'flag-cohorts' && <FlagCohortsTab />}
        {activeTab === 'ai-cli' && <AiCliTab />}
        {activeTab === 'changelog' && <ChangelogEntries />}
        </div>
      </div>
    </AdminLayout>
  );
}


